import React, { useEffect, useState } from 'react';
import { ArrowLeft, Share2, Download } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import DashboardInsights from '../components/dashboardInsights';
import Newsletter from '../components/newsLetter';
import { fetchInsightBySlug } from '../api/blog';
import { Loader } from '../components/ui/loader';

const InsightDetail = () => {
  const { slug } = useParams();
  const [insight, setInsight] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!slug) return; // Early return if no slug

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchInsightBySlug(slug);
        setInsight(data);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (isLoading) {
    return <Loader />;
  }
  
  return (
    <div className="min-h-screen bg-white">
      
      {/* Main Content */}
      {insight && (<main className="pt-16">
        {/* Hero Section */}
        <section className="">
          <div className="text-center mb-[40px] md:mb-[60px] mt-[60px] md:mt-[100px] max-w-[1400px] place-self-center">
            <h1 className="text-3xl mb-[30px] md:text-5xl lg:text-[64px] font-normal leading-[100%] font-serifDisplay px-2 sm:px-4 lg:px-8">
              {insight?.title?.title}<br />
              {insight?.title?.detailTitleDescription}
            </h1>
          </div>

          {/* Hero Image */}
          <div className="mb-8 md:mb-12">
            <img 
              src={insight?.title?.mainImage?.url}
              alt="Tripartite Tug of War illustration"
              className="w-full object-contain"
            />
          </div>
        </section>

        {/* Article Content */}
        <article className="max-w-[70rem] mx-auto px-4 sm:px-6 lg:px-8" >
          {/* Author Info */}
          <div className="flex items-center pb-8 md:pb-12 pt-[20px] md:pt-[25px] border-t-[2.4px] border-t-black/10">
            <img 
              src={insight?.writer?.personPicture?.url}
              alt="Author"
              className="w-10 h-10 md:w-12 md:h-12 rounded-full mr-3 md:mr-4"
            />
            <div className="flex-1">
              <p className="font-bold text-black text-sm md:text-[19.2px]">{insight?.writer?.name}, {insight?.writer?.role} </p>
              <p className="text-sm md:text-[19.2px] mt-[5px]">{insight?.publishedAt &&
                `${new Date(insight.publishedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}`}
              </p>
            </div>
            <div className="flex rounded-[4.2px] shadow-md">
              <button className="border border-[#EAEAEA] border-r-0 border-[1.2px] h-[42px] md:h-[52.8px] w-[70px] md:w-[84px] place-items-center rounded-tl-[4.2px] rounded-bl-[4.2px] grid"
                onClick={() => window.open(insight.writer.facebookUrl, '_blank')}>
                <svg width="20" height="20" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_51_94)">
                  <path d="M12.0001 0.849976C5.37268 0.849976 0 6.22266 0 12.85C0 18.794 4.32626 23.7167 9.9987 24.6699V15.3536H7.10393V12.001H9.9987V9.52898C9.9987 6.6607 11.7506 5.09765 14.3096 5.09765C15.5353 5.09765 16.5886 5.18898 16.8944 5.2292V8.22732L15.1194 8.22818C13.728 8.22818 13.4598 8.88925 13.4598 9.85966V11.9993H16.7798L16.3468 15.3519H13.4598V24.7501C19.397 23.6277 24 18.9794 24 12.8466C24 6.22266 18.6273 0.849976 12.0001 0.849976Z" fill="#010002"/>
                  </g>                
                </svg>
              </button>
              <button className="border border-[#EAEAEA] border-[1.2px] h-[42px] md:h-[52.8px] w-[70px] md:w-[84px] place-items-center rounded-tr-[4.2px] rounded-br-[4.2px] grid"
                onClick={() => window.open(insight.writer.twitterUrl, '_blank')}>
                <svg width="20" height="20" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_959_105)">
                  <rect width="29" height="29" rx="14.5" fill="white"/>
                  <path d="M4.14286 0C1.85781 0 0 1.85781 0 4.14286V24.8571C0 27.1422 1.85781 29 4.14286 29H24.8571C27.1422 29 29 27.1422 29 24.8571V4.14286C29 1.85781 27.1422 0 24.8571 0H4.14286ZM23.3748 5.4375L16.6556 13.1147L24.5594 23.5625H18.371L13.529 17.2252L7.98147 23.5625H4.9067L12.092 15.348L4.51183 5.4375H10.8556L15.2379 11.231L20.3 5.4375H23.3748ZM20.9279 21.7241L9.92991 7.17879H8.09799L19.219 21.7241H20.9279Z" fill="black"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_959_105">
                  <rect width="29" height="29" rx="14.5" fill="white"/>
                  </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
          </div>

          {/* Article Text */}
          <div className="prose prose-lg max-w-none mb-12 md:mb-16">
            <p className="font-normal text-base md:text-[20px] leading-[170%] tracking-[0] mb-6">
              {insight?.firstParagraph}
            </p>

            <h2 className="font-bold text-2xl md:text-[38.4px] leading-[160%] tracking-[0] mb-6">{insight?.firstSection?.heading}</h2>
            
            <p className="font-normal text-base md:text-[20px] leading-[170%] tracking-[0] mb-8">
              <DescriptionRenderer description={insight?.firstSection?.description} />
            </p>

            {/* Image Section */}
            {insight?.firstSection?.image && (<div className="my-8 md:my-12">
              <img 
                src= {insight?.firstSection?.image?.url}
                alt="Business handshake"
                className="w-full object-cover mb-4"
              />
            </div>)}

            <h2 className="font-bold text-2xl md:text-[38.4px] leading-[160%] tracking-[0] mb-6">{insight?.secondSection?.heading}</h2>
            
            <div className="font-normal text-base md:text-[20px] leading-[170%] tracking-[0] mb-8">
              <DescriptionRenderer description={insight?.secondSection?.description} />
            </div>

            {insight?.secondSection?.image && (<div className="my-8 md:my-12">
              <img 
                src= {insight?.secondSection?.image?.url}
                alt="Business handshake"
                className="w-full object-cover mb-4"
              />
            </div>)}

            {insight?.thirdSection?.map((section : any, index : any) => (
              <div key={index} className="mb-12 md:mb-16">
                <h2 className="font-bold text-2xl md:text-[38.4px] leading-[160%] tracking-[0] mb-6">
                  {section?.heading}
                </h2>

                <div className="font-normal text-base md:text-[20px] leading-[170%] tracking-[0] mb-8">
                  <DescriptionRenderer description={section?.description} />
                </div>

                {section?.image && (
                  <div className="my-8 md:my-12">
                    <img
                      src={section.image.url}
                      alt={section?.heading || 'Insight image'}
                      className="w-full object-cover mb-4"
                    />
                  </div>
                )}
              </div>
            ))}
            <div className='font-normal text-base md:text-[20px] leading-[170%] tracking-[0] mt-8 md:mt-10'>Thanks for reading,</div>
            <div className='font-normal text-base md:text-[20px] leading-[170%] tracking-[0]'>{insight?.writer?.name}</div>
            <div className="flex flex-col md:flex-row gap-5 md:gap-0 mt-12 md:mt-[80px] justify-center w-[100%]">
              <button className="border border-[#EAEAEA] border-[1.2px] h-[60px] md:h-[76.8px] w-full md:w-[384px] flex items-center justify-center rounded-[4.2px] shadow-md font-medium text-base md:text-[19.2px] leading-[160%] tracking-[0]"
                onClick={() => window.open(insight.writer.facebookUrl, '_blank')}>
                <svg width="20" height="20" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" className='mr-3 md:mr-[15px]'>
                  <g clipPath="url(#clip0_51_135)">
                    <path d="M12.0001 0.450195C5.37268 0.450195 0 5.82288 0 12.4503C0 18.3943 4.32626 23.3169 9.9987 24.2701V14.9538H7.10393V11.6012H9.9987V9.1292C9.9987 6.26092 11.7506 4.69787 14.3096 4.69787C15.5353 4.69787 16.5886 4.7892 16.8944 4.82942V7.82754L15.1194 7.8284C13.728 7.8284 13.4598 8.48947 13.4598 9.45988V11.5995H16.7798L16.3468 14.9521H13.4598V24.3503C19.397 23.6277 24 18.5796 24 12.4468C24 5.82288 18.6273 0.450195 12.0001 0.450195Z" fill="#010002"/>
                  </g>
                </svg> Share on Facebook
              </button>
              <button className="border border-[#EAEAEA] border-[1.2px] h-[60px] md:h-[76.8px] w-full md:w-[384px] flex items-center justify-center rounded-[4.2px] shadow-md font-medium text-base md:text-[19.2px] leading-[160%] tracking-[0]"
                onClick={() => window.open(insight.writer.linkedinUrl, '_blank')}>
                <div className="bg-black w-5 h-5 md:w-[24px] md:h-[25px] rounded-full grid place-items-center mr-3 md:mr-[15px]">
                  <svg width="11" height="10" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M4.64322 3.69235H7.21193V5.00145H7.2491C7.60682 4.36348 8.48154 3.69235 9.78593 3.69235C12.4984 3.69235 13 5.37038 13 7.55331V12H10.3212V8.05819C10.3212 7.11853 10.3015 5.9089 8.92823 5.9089C7.53339 5.9089 7.31963 6.9318 7.31963 7.98974V12H4.6432V3.69235H4.64322ZM2.78608 1.3847C2.78608 2.14907 2.16207 2.76939 1.39315 2.76939C0.624233 2.76939 0 2.14907 0 1.3847C0 0.620322 0.624233 0 1.39315 0C2.16207 0 2.78608 0.620322 2.78608 1.3847ZM0 3.69235H2.78608V12H0V3.69235Z" fill="white"/>
                  </svg>
                </div>
                Share on LinkedIn
              </button>
            </div>
            <div className='mt-16 md:mt-24' style={{
              width: '100%',
              height: '1.2px',
              backgroundImage: 'linear-gradient(to right, black 1.2px, transparent 1.2px)',
              backgroundSize: '4.8px 1.2px',
            }}></div>

          </div>
          <div className='block md:flex mb-16 md:mb-24'>
            <img 
              src={insight?.writer?.personPicture?.url}
              alt="Author"
              className="w-[60px] h-[60px] md:w-[84px] md:h-[84px] rounded-full mr-4 md:mr-6 self-center place-self-center mb-4 md:mb-0"
            />
            <div className="flex-1">
              <p className="font-bold text-base md:text-[19.2px] leading-[160%] tracking-[5%]">{insight?.writer?.name?.toUpperCase()} {' '}
                <span className="font-normal text-base md:text-[19.2px] leading-[160%] tracking-[0]">{insight?.writer?.introduction}
                </span>
              </p>
            </div>
          </div>
        </article>

        <DashboardInsights/>
        <Newsletter/>
      </main>)}
    </div>
  );
};

export default InsightDetail;

const DescriptionRenderer = ({ description } : any) => {
  return (
    <>
      {description?.map((item : any, index : any) => {
        // Handle Paragraphs
        if (item.type === "paragraph") {
          return (
            <p
              key={index}
              className={`font-normal text-base md:text-[20px] leading-[170%] tracking-[0] pb-4 ${
                item.children[0]?.bold ? 'pt-5' : ''
              }`}
            >
              {item.children[0]?.bold ? (
                <strong>{item.children[0].text}</strong>
              ) : (
                item.children[0]?.text
              )}
            </p>
          );
        }

        // Handle Ordered Lists
        if (item.type === "list" && item.format === "ordered") {
          return (
            <ol key={index} className="list-decimal pl-5 md:pl-[20px] mb-6 md:mb-8">
              {item.children?.map((listItem : any, idx : any) => (
                <li key={idx} className="mb-2 md:mb-4">
                  {listItem.children?.map((child : any, childIdx : any) => (
                    child.bold ? (
                      <strong key={childIdx} className="block">{child.text}</strong>
                    ) : (
                      <span key={childIdx} className="block">{child.text}</span>
                    )
                  ))}
                </li>
              ))}
            </ol>
          );
        }

        // Handle Unordered Lists
        if (item.type === "list" && item.format === "unordered") {
          return (
            <ul key={index} className="list-disc pl-5 md:pl-[20px] mb-4 md:mb-4">
              {item.children?.map((listItem : any, idx : any) => (
                <li key={idx} className="mb-2 md:mb-4">
                  {listItem.children[0]?.text}
                </li>
              ))}
            </ul>
          );
        }

        return null;
      })}
    </>
  );
};